import type { ReactNode } from "react";

import { highlightText, hasSearchableQueryTokens } from "@/lib/search";
import { cn } from "@/lib/utils";

export interface GatekeeperAnnotationSegment {
  kind: "text" | "annotation";
  value: string;
}

export type GatekeeperAnnotatedTextVariant = "bubble" | "inline";

const ANNOTATION_PATTERN = /\[\[([^\]]+)\]\]/g;

export function hasGatekeeperAnnotations(text: string): boolean {
  return text.includes("[[");
}

/** Length of plain (non-annotation) text only — used for ClampText budget. */
export function gatekeeperPlainTextLength(text: string): number {
  return splitGatekeeperAnnotations(text).reduce(
    (sum, segment) =>
      segment.kind === "text" ? sum + segment.value.length : sum,
    0
  );
}

/**
 * Truncates plain-text segments to `maxLength` while always preserving every
 * `[[annotation]]` marker (pastilles remain visible when clamped).
 */
export function clampGatekeeperAnnotatedText(
  text: string,
  maxLength: number
): string {
  const segments = splitGatekeeperAnnotations(text);
  let remaining = maxLength;
  let textOmitted = false;
  const parts: string[] = [];

  for (const segment of segments) {
    if (segment.kind === "annotation") {
      parts.push(`[[${segment.value}]]`);
      continue;
    }

    if (textOmitted) {
      continue;
    }

    if (remaining <= 0) {
      if (segment.value.length > 0) {
        parts.push("…");
        textOmitted = true;
      }
      continue;
    }

    if (segment.value.length <= remaining) {
      parts.push(segment.value);
      remaining -= segment.value.length;
      continue;
    }

    parts.push(segment.value.slice(0, remaining));
    parts.push("…");
    textOmitted = true;
    remaining = 0;
  }

  return parts.join("");
}

/** Splits user text into plain spans and [[annotation]] spans. */
export function splitGatekeeperAnnotations(
  text: string
): GatekeeperAnnotationSegment[] {
  const segments: GatekeeperAnnotationSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(ANNOTATION_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      segments.push({
        kind: "text",
        value: text.slice(lastIndex, index),
      });
    }
    segments.push({
      kind: "annotation",
      value: match[1] ?? "",
    });
    lastIndex = index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ kind: "text", value: text.slice(lastIndex) });
  }

  if (segments.length === 0) {
    return [{ kind: "text", value: text }];
  }

  return segments;
}

const ANNOTATION_VARIANT_CLASS: Record<GatekeeperAnnotatedTextVariant, string> =
  {
    bubble:
      "border-primary-foreground/25 bg-primary-foreground/15 text-primary-foreground shadow-sm text-[0.8125rem] px-1.5 py-0.5",
    inline:
      "border-muted-foreground/30 bg-muted-foreground/5 text-muted-foreground !leading-tight text-[0.8em] px-1.5 py-0.5",
  };

function renderSegmentContent(value: string, searchQuery?: string): ReactNode {
  if (searchQuery && hasSearchableQueryTokens(searchQuery)) {
    return highlightText(value, searchQuery);
  }
  return value;
}

function GatekeeperQualifiedSegment({
  value,
  variant,
  searchQuery,
}: {
  value: string;
  variant: GatekeeperAnnotatedTextVariant;
  searchQuery?: string;
}) {
  return (
    <span
      className={cn(
        "inline-block items-center rounded-md border mt-1 mx-0.25 align-baseline font-semibold leading-snug",
        ANNOTATION_VARIANT_CLASS[variant]
      )}>
      {renderSegmentContent(value, searchQuery)}
    </span>
  );
}

export function GatekeeperAnnotatedText({
  text,
  variant = "bubble",
  className,
  searchQuery,
}: {
  text: string;
  variant?: GatekeeperAnnotatedTextVariant;
  className?: string;
  /** Optional search query; highlights matches in text and annotation badges. */
  searchQuery?: string;
}) {
  const keyCounts = new Map<string, number>();

  return (
    <span
      className={cn(
        "whitespace-pre-wrap wrap-break-word leading-snug",
        className
      )}>
      {splitGatekeeperAnnotations(text).map((segment) => {
        const base = `${segment.kind}:${segment.value}`;
        const seen = (keyCounts.get(base) ?? 0) + 1;
        keyCounts.set(base, seen);
        const key = seen === 1 ? base : `${base}#${seen}`;
        if (segment.kind === "annotation") {
          return (
            <GatekeeperQualifiedSegment
              key={key}
              value={segment.value}
              variant={variant}
              searchQuery={searchQuery}
            />
          );
        }
        return (
          <span key={key}>
            {renderSegmentContent(segment.value, searchQuery)}
          </span>
        );
      })}
    </span>
  );
}

export function renderGatekeeperAnnotatedText(
  text: string,
  variant: GatekeeperAnnotatedTextVariant = "bubble",
  searchQuery?: string
): ReactNode {
  return (
    <GatekeeperAnnotatedText
      text={text}
      variant={variant}
      searchQuery={searchQuery}
    />
  );
}
